import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { Seo, AdSenseUnit } from '../shared';
import HomeFestivalsBand from '../home/HomeFestivalsBand';
import { HomeHashScroller } from '../home/HomeRedirects';
import HomeExperienceBand from '../booking/HomeExperienceBand';
import InternationalCardBanner from '../booking/InternationalCardBanner';
import HomeDiscoverPanel from '../home/HomeDiscoverPanel';
import HomePlanPanel from '../home/HomePlanPanel';
import HomeJourneyPanel from '../home/HomeJourneyPanel';
import { nepalImageSrc, nepalImageAlt } from '../../data/nepalImages';
import { AD_SLOTS } from '../../utils/adsense';
import { SITE_URL } from '../../constants';
import { fetchPublishedJourneyCount } from '../../utils/cmsContentLoader';
import '../pages/homepage.css';
import '../home/home-editorial.scss';

const HOME_SEO_SCHEMA = [
	{
		'@context': 'https://schema.org',
		'@type': 'WebSite',
		name: 'Visit All Nepal',
		alternateName: ['VisitAllNepal', 'visit all nepal', 'visit nepal', 'nepal travel', 'nepal tourism', 'visitallnepal.com'],
		url: SITE_URL,
		description: 'Guided journeys, trekking guides, destinations, visa help, and trip planning for Nepal travel.',
		potentialAction: {
			'@type': 'SearchAction',
			target: `${SITE_URL}/?q={search_term_string}`,
			'query-input': 'required name=search_term_string',
		},
	},
	{
		'@context': 'https://schema.org',
		'@type': 'Organization',
		name: 'Visit All Nepal',
		url: SITE_URL,
		logo: `${SITE_URL}/logo.png`,
		email: 'admin@visitallnepal.com',
		areaServed: 'Nepal',
	},
	{
		'@context': 'https://schema.org',
		'@type': 'TravelAgency',
		name: 'Visit All Nepal',
		url: SITE_URL,
		logo: `${SITE_URL}/logo.png`,
		email: 'admin@visitallnepal.com',
		areaServed: 'Nepal',
	},
];

function VendorHero({ name }) {
	return (
		<header className="hero-experience hero-experience--vendor" role="banner">
			<div className="hero-experience__bg" aria-hidden="true">
				<img className="hero-experience__image" src="/images/hero-nepal.jpg" alt="" fetchPriority="high" />
			</div>
			<div className="hero-experience__overlay" aria-hidden="true" />
			<div className="hero-experience__content">
				<span className="hero-experience__badge">Partner</span>
				<h1 className="hero-experience__title">Hi, {name}</h1>
				<p className="hero-experience__sub">Your partner dashboard — earnings, assignments &amp; payment history.</p>
				<Link to="/partner" className="hero-experience__cta hero-experience__cta--vendor">
					Open Vendor Dashboard →
				</Link>
			</div>
		</header>
	);
}

function HomePage({ currentUser }) {
	const isVendor = currentUser && currentUser.role === 'Vendor' && currentUser.email !== undefined;
	const vendorName = (currentUser && currentUser.name) || 'Partner';
	const [journeyCount, setJourneyCount] = useState(null);

	useEffect(() => {
		fetchPublishedJourneyCount().then(setJourneyCount);
	}, []);

	const journeyCountLabel = journeyCount != null ? String(journeyCount) : 'guided';

	if (isVendor) {
		return (
			<>
				<Seo path="/" description="VisitAllNepal partner dashboard" />
				<VendorHero name={vendorName} />
			</>
		);
	}

	return (
		<div className="home-magazine home-magazine--onepage">
			<Seo
				path="/"
				titleExact="Visit All Nepal"
				description={`Book Nepal domestic flights, group treks, homestays & tours with your Visa or Mastercard (USD/NPR). Free travel guides — ${journeyCountLabel} treks, visa help, and trip planning.`}
				keywords="visit all nepal, nepal, nepal travel, visit nepal, travel nepal, nepal tourism, nepal trekking, nepal guide, everest base camp, annapurna trek, nepal visa, nepal festivals, pokhara, kathmandu, things to do in nepal"
				schema={HOME_SEO_SCHEMA}
			/>

			<header className="home-hero home-hero--simple home-hero--booking" role="banner">
				<div className="home-hero__media" aria-hidden="true">
					<img src={nepalImageSrc('heroEverest')} alt={nepalImageAlt('heroEverest')} fetchPriority="high" />
					<div className="home-hero__scrim home-hero__scrim--booking" />
				</div>
				<div className="home-hero__content home-hero__content--booking">
					<div className="home-hero__booking-stack">
						<InternationalCardBanner
							layout="hero"
							showScopes
							showLocalWallets={false}
							headlineAsH1
						/>
						<div className="home-hero__book-actions">
							<Link to="/flights" className="home-hero__book-cta home-hero__book-cta--primary">
								Domestic flights
							</Link>
							<Link to="/book-experiences" className="home-hero__book-cta home-hero__book-cta--ghost">
								Book treks &amp; tours
							</Link>
						</div>
					</div>
				</div>
			</header>

			<HomeExperienceBand />

			<section className="home-editorial-intro container" aria-label="About this site">
				<h2 className="home-editorial-intro__title">Original Nepal travel content</h2>
				<p>
					Visit All Nepal is a free travel guide and trip planner for Nepal. We publish guided step-by-step
					journeys for major treks, destination articles, festival guides, visa and permit help, packing lists,
					budget breakdowns, and safety advice — written for travelers planning their first or fifth visit.
				</p>
				<p>
					Our guides cover practical topics you will actually need: how to get a visa on arrival at Tribhuvan
					Airport, which permits your trek requires, what teahouse lodging costs on the Everest trail, when
					Dashain and Tihar fall this year, and how to choose between Annapurna Base Camp and Ghorepani Poon Hill
					for a first trek. Content is updated against official sources where rules apply.
				</p>
				<p>
					We also operate a booking service — group departures, private tours, and shared intercity transport —
					so you can reserve with a verified operator after you have read the guides and chosen your route.
					Booking is optional; the editorial content stays free.
				</p>
				<p className="mb-0">
					Browse <a href="/journeys">guided journeys</a>, <a href="/festivals">festivals</a>,{' '}
					<a href="/trekking">trekking guides</a>, and our <a href="/guides">travel guides directory</a>.
					When you are ready to travel, <Link to="/book-experiences">book a trip</Link> through Visit All Nepal.
				</p>
			</section>

			<HomeFestivalsBand />

			<AdSenseUnit slot={AD_SLOTS.home} className="adsense-unit--in-content container" layout="home" />

			<HomeHashScroller />

			<main className="home-onpage">
				<section id="journey" className="home-onpage__section" aria-label="Guided journey">
					<HomeJourneyPanel />
				</section>
				<section id="places" className="home-onpage__section" aria-label="Places in Nepal">
					<HomeDiscoverPanel />
				</section>
				<section id="plan" className="home-onpage__section" aria-label="Plan your trip">
					<HomePlanPanel />
				</section>
			</main>
		</div>
	);
}

const mapStateToProps = ({ userStore }) => ({
	currentUser: userStore.currentUser,
});

export default connect(mapStateToProps)(HomePage);
